import { useState } from "react";
import {
  Box,
  Card,
  Typography,
  Radio,
  Pagination,
  Drawer,
  ThemeProvider,
  createTheme,
  IconButton,
  CircularProgress,
  TextField,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import "@fontsource/manrope";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";

const theme = createTheme({
  typography: {
    fontFamily: "Manrope, Arial, sans-serif",
  },
});

const ConfluencePagesComponentPage = ({
  taskType,
  pagesLoading,
  setImportPageOpen,
  pagesList,
  setPagesList,
  selectedPage,
  setSelectedPage,
  onPageSelected,
  setSelectedSpace,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const [pageItem, setPageItem] = useState(selectedPage);
  const [saveLoading, setSaveLoading] = useState(false);
  const pagesPerPage = 6;

  const handleRadioChange = (pageItem) => {
    setPageItem(pageItem);
  };

  const handleOk = () => {
    setSelectedPage(pageItem);
    if (onPageSelected) {
      onPageSelected(pageItem);
    }
  };

  // const handlePageChange = (event, value) => setPage(value);

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setImportPageOpen(false);
  };

  const handleBack = () => {
    setSelectedSpace(null);
    setPagesList([]);
    setSelectedPage(null);
  };

  const filteredPages = pagesList.filter((pageItem) =>
    pageItem.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // const displayedPages = filteredPages.slice(
  //   (page - 1) * pagesPerPage,
  //   page * pagesPerPage
  // );

  return (
    <ThemeProvider theme={theme}>
      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={handleCloseDrawer}
        slotProps={{
          paper: {
            sx: {
              width: {
                xs: "90vw", // Mobile
                sm: "70vw", // Tablet
                md: "50vw", // Small laptops
                lg: "40vw", // Desktop
              },
              maxWidth: "none",
            },
          },
        }}
      >
        <Box
          sx={{
            p: { xs: 2, sm: 3 },
            display: "flex",
            flexDirection: "column",
            height: "100%",
            overflow: "hidden",
          }}
        >
          {/* Header with Back + Close */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <ArrowBackIosNewIcon
                fontSize="small"
                onClick={handleBack}
                sx={{ mr: 1, cursor: "pointer" }}
              />
              <Typography variant="h6">{taskType} Page</Typography>
            </Box>

            <IconButton onClick={handleCloseDrawer}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Search Row */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              width: "100%",
            }}
          >
            {/* <Typography variant="subtitle2" color="text.secondary">
              Choose a page to continue
            </Typography> */}
            <TextField
              size="small"
              placeholder="Search pages"
              value={searchTerm}
              disabled={pagesList.length === 0}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              fullWidth
              // sx={{
              //   width: "100%" // responsive widths
              // }}
            />
          </Box>

          {/* Content */}
          {pagesList.length === 0 ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {pagesLoading ? (
                <CircularProgress />
              ) : (
                <Typography>No pages found</Typography>
              )}
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: 1.5,
                  flex: 1,
                  overflowY: "auto",
                }}
              >
                {filteredPages.map((pageItemProp) => (
                  <Card
                    key={pageItemProp.id}
                    variant="outlined"
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      height: 90,
                      p: 1,
                      borderRadius: 2,
                      cursor: "pointer",
                    }}
                    onClick={() => handleRadioChange(pageItemProp)} // clicking card also selects
                  >
                    <Box sx={{ display: "flex", alignItems: "center", ml: -1 }}>
                      <Radio
                        checked={pageItem?.id === pageItemProp.id} // ✅ check against temp state
                        value={pageItemProp.id}
                        name="confluence-page-radio"
                        onChange={() => handleRadioChange(pageItemProp)}
                      />
                      <Box>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          noWrap
                          sx={{ maxWidth: 180 }}
                        >
                          {pageItemProp.title}
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                ))}
              </Box>

              {/* Footer Buttons */}
              <Box
                sx={{
                  flexShrink: 0,
                  pt: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 2,
                  backgroundColor: "#fff",
                }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={handleCloseDrawer}
                  sx={{
                    width: { xs: "100%", sm: "60px", md: "80px" },
                    textTransform: "none",
                    borderRadius: "8px",
                    borderWidth: "2px",
                    borderColor: "#0094FB",
                    "&:hover": { backgroundColor: "#ffffff" },
                    color: "#0094FB",
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleOk}
                  disabled={pagesLoading || !pageItem}
                  sx={{
                    width: { xs: "100%", sm: "60px", md: "80px" },
                    textTransform: "none",
                    borderRadius: "8px",
                    backgroundColor: "#0094FB",
                    "&:hover": { backgroundColor: "#0094FB" },
                    "&.Mui-disabled": {
                      backgroundColor: "#0094FB",
                      color: "#fff",
                      opacity: 0.6,
                    },
                  }}
                >
                  {saveLoading ? <CircularProgress size={20} /> : "Confirm"}
                </Button>
              </Box>

              {/* <Pagination
                count={Math.ceil(filteredPages.length / pagesPerPage)}
                page={page}
                onChange={handlePageChange}
                sx={{ mt: 2, display: "flex", justifyContent: "center" }}
              /> */}
            </>
          )}
        </Box>
      </Drawer>
    </ThemeProvider>
  );
};

export default ConfluencePagesComponentPage;
